import './scss/app.scss';
import './css/tom-select.css';
import 'tippy.js/dist/tippy.css';

import Split from 'split.js';
import Alpine from 'alpinejs';
import Focus from "@alpinejs/focus";
import AlpineFloatingUI from "@awcodes/alpine-floating-ui";
import Tooltip from "@ryangjchandler/alpine-tooltip";
import TomSelect from 'tom-select';
// Using require
import hljs from 'highlight.js/lib/core';
import json from 'highlight.js/lib/languages/json';
hljs.registerLanguage('json', json);
// Load any languages you need

Alpine.plugin(Focus);
Alpine.plugin(AlpineFloatingUI);
Alpine.plugin(Tooltip);

// Alpine JS init
window.Alpine = Alpine;

document.addEventListener('alpine:init', () => {
    Alpine.data('tabs', () => ({
        tabs: [],
        currentTab: null,
        isCurrent(tab) {
            return this.currentTab === tab;
        },
        register(name) {
            this.tabs.push(name);
        },
    }));

    Alpine.data('jobStatusWatcher', (statusUrl, initiallyRunning) => ({
        jobFinished: false,
        // Seeded from the server render: a job that ends before the first poll must still offer a reload.
        wasRunning: initiallyRunning,
        init() {
            setTimeout(() => this.poll(), 3000);
        },
        poll() {
            fetch(statusUrl)
                .then(r => r.json())
                .then(data => {
                    const isRunning = data.has_pending_tasks || data.has_started_tasks;
                    // Always sync: the server-rendered initial state may already be stale by the first poll.
                    window.dispatchEvent(new CustomEvent('job-status-updated', { detail: data }));
                    if (isRunning) {
                        this.wasRunning = true;
                    } else if (this.wasRunning) {
                        this.jobFinished = true;
                        window.dispatchEvent(new CustomEvent('job-status-finished'));
                        return;
                    }
                    // Keep polling while idle too, in case the job is started later (e.g. by someone else).
                    setTimeout(() => this.poll(), 3000);
                })
                .catch(() => setTimeout(() => this.poll(), 3000));
        },
    }));
});

// Registering Alpine.data() must happen before start() so components are
// available by the time Alpine walks the DOM for x-data.
Alpine.start();

(function () {
    const selects = document.querySelectorAll('select:not(.skip-tomselect)');
    selects.forEach(select => {
        new TomSelect(select, {
            plugins: select.multiple ? ['remove_button'] : [],
        });
    });
})();

const SECRET_PATTERN = /\{\{secret:(\w+)\}\}/g;

const buildSecretButton = (placeholder, secretValue) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className =
        'inline-flex items-center rounded border border-warning-300 bg-warning-100 px-1 text-warning-900 shadow-sm';
    button.setAttribute('title', 'Click to decrypt. Alt + Click to copy secret.');
    button.setAttribute('aria-label', 'Click to decrypt. Alt + Click to copy secret.');
    button.dataset.secret = secretValue;
    button.dataset.placeholder = placeholder;
    button.dataset.revealed = 'false';
    button.textContent = placeholder;
    button.addEventListener('click', (event) => {
        if (event.altKey) {
            navigator.clipboard?.writeText(secretValue);
            return;
        }
        const revealed = button.dataset.revealed === 'true';
        button.dataset.revealed = (!revealed).toString();
        button.textContent = revealed ? placeholder : secretValue;
    });
    return button;
};

const buildMaskedSecretButton = (placeholder) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className =
        'inline-flex items-center rounded border border-gray-300 bg-gray-100 px-1 text-gray-600 shadow-sm';
    button.setAttribute('title', 'You do not have permission to decrypt this secret.');
    button.setAttribute('aria-label', 'You do not have permission to decrypt this secret.');
    button.dataset.placeholder = placeholder;
    button.dataset.revealed = 'false';
    button.textContent = placeholder;
    button.addEventListener('click', () => {
        const revealed = button.dataset.revealed === 'true';
        button.dataset.revealed = (!revealed).toString();
        button.textContent = revealed ? placeholder : '********';
    });
    return button;
};

const injectSecretButtons = (codeElement) => {
    const rawMap = codeElement.dataset.secretMap;
    if (!rawMap) {
        return;
    }
    let secretMap = {};
    try {
        secretMap = JSON.parse(rawMap);
    } catch (error) {
        return;
    }
    if (!Object.keys(secretMap).length) {
        return;
    }
    const walker = document.createTreeWalker(codeElement, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) {
        nodes.push(walker.currentNode);
    }
    nodes.forEach((node) => {
        const text = node.nodeValue;
        if (!text || !text.includes('{{secret:')) {
            return;
        }
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;
        let match;
        SECRET_PATTERN.lastIndex = 0;
        while ((match = SECRET_PATTERN.exec(text))) {
            const placeholder = match[0];
            const key = match[1];
            const hasSecret = Object.prototype.hasOwnProperty.call(secretMap, key);
            const secretValue = hasSecret ? secretMap[key] : null;
            if (match.index > lastIndex) {
                fragment.append(text.slice(lastIndex, match.index));
            }
            if (!hasSecret) {
                fragment.append(placeholder);
            } else if (secretValue === null || secretValue === undefined) {
                fragment.append(buildMaskedSecretButton(placeholder));
            } else {
                fragment.append(buildSecretButton(placeholder, secretValue));
            }
            lastIndex = match.index + placeholder.length;
        }
        if (lastIndex < text.length) {
            fragment.append(text.slice(lastIndex));
        }
        node.parentNode.replaceChild(fragment, node);
    });
};

const wireCopyButtons = () => {
    document.querySelectorAll('[data-copy-target]').forEach((button) => {
        button.addEventListener('click', () => {
            const targetId = button.dataset.copyTarget;
            const target = targetId ? document.getElementById(targetId) : null;
            if (!target) {
                return;
            }
            const textToCopy =
                target.dataset.rawJson ||
                target.dataset.copyValue ||
                target.dataset.secret ||
                (target.tagName === 'A' && target.href ? target.href.trim() : '') ||
                target.value ||
                (target.textContent ? target.textContent.trim() : '');
            if (!textToCopy) {
                return;
            }
            navigator.clipboard?.writeText(textToCopy);
        });
    });
};

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('pre code').forEach((block) => {
        if (!block.dataset.rawJson) {
            block.dataset.rawJson = block.textContent || '';
        }
        hljs.highlightElement(block);
    });
    document.querySelectorAll('code[data-secret-map]').forEach(injectSecretButtons);
    wireCopyButtons();
    initSplitPanels();
});

function initSplitPanels() {
    const isMdUp = window.matchMedia('(min-width: 768px)').matches;
    document.querySelectorAll('[data-split]').forEach(container => {
        const left = container.querySelector('[data-split-left]');
        const right = container.querySelector('[data-split-right]');
        if (!left || !right || !isMdUp) return;
        Split([left, right], {
            sizes: [50, 50],
            minSize: 150,
            gutterSize: 11,
            gutter: () => {
                const el = document.createElement('div');
                el.style.cssText = [
                    'background-color:#eee',
                    'background-image:url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAUAAAAeCAYAAADkftS9AAAAIklEQVQoU2M4c+bMfxAGAgYYmwGrIIiDjrELjpo5aiZeMwF+yNnOs5KSvgAAAABJRU5ErkJggg==")',
                    'background-repeat:no-repeat',
                    'background-position:50%',
                    'cursor:col-resize',
                    'flex-shrink:0',
                    'transition:background-color 150ms',
                ].join(';');
                el.addEventListener('mouseenter', () => { el.style.backgroundColor = '#dbeafe'; });
                el.addEventListener('mouseleave', () => { el.style.backgroundColor = '#eee'; });
                return el;
            },
        });
    });
}
